import { Navigate } from 'react-router-dom'
import { useKyc } from '@/src/contexts/KycContext'

interface KycGuardProps {
  children: React.ReactNode
}

/**
 * KycGuard - Redirects to KYC verification page for all non-approved KYC statuses.
 *
 * Allows access only when KYC is fully approved by admin.
 */
export function KycGuard({ children }: KycGuardProps) {
  const { loading, requiresKyc } = useKyc()

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังตรวจสอบสถานะ...</p>
        </div>
      </div>
    )
  }

  // Redirect to verification page for not_started, pending, and rejected
  if (requiresKyc) {
    return <Navigate to="/auth/verify" replace />
  }

  // Only approved users can render children
  return <>{children}</>
}

/**
 * KycVerifiedGuard - Requires KYC to be fully APPROVED.
 *
 * Use this guard for sensitive features that require verified identity:
 * - Slip verification (slips-manage)
 * - API Connect (api-connect/*)
 * - Connections (connects)
 *
 * Redirects to /auth/verify for all non-approved users.
 */
export function KycVerifiedGuard({ children }: KycGuardProps) {
  const { loading, kycStatus } = useKyc()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังตรวจสอบสถานะ...</p>
        </div>
      </div>
    )
  }

  // Block if KYC not fully approved — covers not-submitted, pending, and rejected
  if (!kycStatus?.verified) {
    return <Navigate to="/auth/verify" replace />
  }

  return <>{children}</>
}
